import { useEffect, useRef, useState, type Dispatch, type SetStateAction } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";

import { BackNavigationGuard } from "./BackNavigationGuard";
import { BetaBanner } from "../billing/BetaBanner";
import { api } from "../../api/client";
import { useEntitlements } from "../../hooks/useEntitlements";
import type { AppContentStatus } from "../../lib/deletion";
import {
  ConnectionStatusButton,
  ConnectionStatusModal,
  OnlineToast,
  useOnlineStatus,
} from "./ConnectionStatus";
import {
  SampleDataButton,
  SampleDataModal,
  fetchSampleDataOpenState,
  type SampleStatus,
} from "../modals/SampleDataModal";
import { ModalHistoryProvider } from "../../lib/modalHistory";
import { clearSoftSession } from "../../lib/offline/offlineAuth";
import { clearStoredTagDisplay, TagDisplayProvider } from "../tags/EarTag";
import { subscribePwaBlock, type PwaBlockReason } from "../../pwa";

type NavItem = {
  to?: string;
  label: string;
  testId?: string;
  end?: boolean;
  children?: NavItem[];
};

type NavSection = {
  id: string;
  label: string;
  items: NavItem[];
};

const SPA_NAV_SECTIONS: NavSection[] = [
  {
    id: "daily",
    label: "Codzienna praca",
    items: [
      { to: "/", label: "Pulpit", testId: "nav-home", end: true },
      { to: "/animals", label: "Zwierzęta", testId: "nav-animals", end: false },
      {
        label: "Rozród",
        testId: "nav-reproduction-toggle",
        children: [
          {
            to: "/reproduction",
            label: "Aktywne grupy",
            testId: "nav-reproduction",
            end: true,
          },
          {
            to: "/reproduction/breeding",
            label: "Stanówka",
            testId: "nav-breeding",
            end: true,
          },
          {
            to: "/reproduction/births",
            label: "Wykoty",
            testId: "nav-lambings",
            end: false,
          },
        ],
      },
      { to: "/controls", label: "Kontrole", testId: "nav-tags", end: false },
    ],
  },
  {
    id: "farm",
    label: "Gospodarstwo",
    items: [
      { to: "/resources", label: "Zasoby", testId: "nav-resources", end: false },
      { to: "/irz", label: "IRZ", testId: "nav-irz", end: true },
      { to: "/reports", label: "Raporty", testId: "nav-reports", end: true },
      { to: "/expenses", label: "Finanse", testId: "nav-expenses", end: true },
    ],
  },
];

const SPA_NAV_FOOTER: NavItem[] = [
  { to: "/guide", label: "Przewodnik", testId: "nav-guide", end: true },
  { to: "/settings", label: "Ustawienia", testId: "nav-settings", end: true },
];

function sectionItems(): NavItem[] {
  return SPA_NAV_SECTIONS.flatMap((section) => section.items);
}

function flattenNav(items: NavItem[]): NavItem[] {
  return items.flatMap((item) => [
    ...(item.to ? [item] : []),
    ...(item.children ?? []),
  ]);
}

function allNavItems(): NavItem[] {
  return [...flattenNav(sectionItems()), ...SPA_NAV_FOOTER];
}

function navItemActive(pathname: string, item: NavItem): boolean {
  if (!item.to) {
    return Boolean(item.children?.some((child) => navItemActive(pathname, child)));
  }
  if (item.children?.length) {
    return pathname === item.to || pathname.startsWith(`${item.to}/`);
  }
  if (item.end) {
    return pathname === item.to;
  }
  return pathname === item.to || pathname.startsWith(`${item.to}/`);
}

function pageTitle(pathname: string): string {
  if (pathname === "/statistics" || pathname.startsWith("/statistics/")) {
    return "Statystyki";
  }
  const matches = allNavItems().filter(
    (item) => item.to && navItemActive(pathname, item),
  );
  matches.sort((a, b) => (b.to?.length ?? 0) - (a.to?.length ?? 0));
  return matches[0]?.label ?? "AgroHodowca";
}

function renderNavItem(
  item: NavItem,
  pathname: string,
  openGroups: Record<string, boolean>,
  setOpenGroups: Dispatch<SetStateAction<Record<string, boolean>>>,
) {
  if (item.children?.length) {
    const groupOpen = Boolean(openGroups[item.label]);
    const groupActive = navItemActive(pathname, item);
    return (
      <div
        key={item.label}
        className={groupActive ? "nav-group nav-group--active" : "nav-group"}
      >
        <button
          type="button"
          className={
            groupActive
              ? "nav-link nav-link--toggle nav-link--section"
              : "nav-link nav-link--toggle"
          }
          aria-expanded={groupOpen}
          data-testid={item.testId}
          onClick={() =>
            setOpenGroups((current) => ({
              ...current,
              [item.label]: !current[item.label],
            }))
          }
        >
          <span className="nav-link__label">{item.label}</span>
          <span
            className={
              groupOpen
                ? "nav-link__chevron nav-link__chevron--open"
                : "nav-link__chevron"
            }
            aria-hidden="true"
          />
        </button>
        {groupOpen ? (
          <div className="nav-group__children">
            {item.children.map((child) =>
              child.to ? (
                <NavLink
                  key={child.to}
                  to={child.to}
                  end={child.end}
                  data-testid={child.testId}
                  className={({ isActive }) =>
                    isActive
                      ? "nav-link nav-link--child nav-link--active"
                      : "nav-link nav-link--child"
                  }
                >
                  <span className="nav-link__label">{child.label}</span>
                </NavLink>
              ) : null,
            )}
          </div>
        ) : null}
      </div>
    );
  }

  if (!item.to) return null;

  return (
    <NavLink
      key={item.to}
      to={item.to}
      end={item.end}
      data-testid={item.testId}
      className={({ isActive }) =>
        isActive ? "nav-link nav-link--active" : "nav-link"
      }
    >
      <span className="nav-link__label">{item.label}</span>
    </NavLink>
  );
}

const BLOCK_TEXT: Record<PwaBlockReason, string> = {
  offline: "Połącz się z siecią przed aktualizacją.",
  "sync-failed":
    "Nie udało się zsynchronizować wydatków offline. Połącz się z siecią przed aktualizacją.",
};

const MOBILE_QUERY = "(max-width: 768px)";

type SampleInitial =
  | { kind: "main"; status: SampleStatus }
  | { kind: "result"; title: string; message: string };

export function Layout() {
  const location = useLocation();
  const mainRef = useRef<HTMLDivElement>(null);
  const [block, setBlock] = useState<PwaBlockReason | null>(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const { online, showToast } = useOnlineStatus();
  const { entitlements, isPlatformAdmin } = useEntitlements();
  const [connectionOpen, setConnectionOpen] = useState(false);
  const [sampleBusy, setSampleBusy] = useState(false);
  const [sampleOpen, setSampleOpen] = useState(false);
  const [sampleInitial, setSampleInitial] = useState<SampleInitial | null>(null);
  const [contentKey, setContentKey] = useState(0);
  const [appContentStatus, setAppContentStatus] = useState<AppContentStatus | null>(null);
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(
      sectionItems()
        .filter((item) => item.children?.length)
        .map((item) => [item.label, navItemActive(location.pathname, item)]),
    ),
  );

  useEffect(() => {
    void api.GET("/api/data/clear-app-content/status").then((result) => {
      if (!result.error) {
        setAppContentStatus(result.data as AppContentStatus);
      }
    });
  }, [contentKey, location.pathname]);

  useEffect(() => subscribePwaBlock(setBlock), []);

  useEffect(() => {
    setMobileOpen(false);
    setOpenGroups((current) => {
      const next = { ...current };
      for (const item of sectionItems()) {
        if (item.children?.length && navItemActive(location.pathname, item)) {
          next[item.label] = true;
        }
      }
      return next;
    });
    // Scroll lives on .layout__main (overflow: auto), not window — reset on route change
    // so mobile navigation always lands at the top of the new page.
    const main = mainRef.current;
    if (main) {
      main.scrollTop = 0;
      main.scrollTo(0, 0);
    }
    window.scrollTo(0, 0);
  }, [location.pathname]);

  useEffect(() => {
    function onPopState() {
      setMobileOpen(false);
    }
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  function toggleSidebar() {
    if (window.matchMedia(MOBILE_QUERY).matches) {
      setMobileOpen((open) => !open);
      setCollapsed(false);
      return;
    }
    setCollapsed((value) => !value);
    setMobileOpen(false);
  }

  async function openSampleData() {
    setSampleBusy(true);
    const initial = await fetchSampleDataOpenState();
    setSampleBusy(false);
    setSampleInitial(initial);
    setSampleOpen(true);
  }

  async function logout() {
    clearSoftSession();
    clearStoredTagDisplay();
    try {
      // Poza scope SW (/app/) — pewne trafienie w serwer nawet ze starym SW.
      await fetch("/logout", { credentials: "same-origin", cache: "no-store" });
    } catch {
      /* i tak wychodzimy na logowanie */
    }
    window.location.replace("/app/login");
  }

  const layoutClass = [
    "layout",
    mobileOpen ? "layout--sidebar-open" : "",
    collapsed ? "layout--sidebar-collapsed" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <TagDisplayProvider>
    <ModalHistoryProvider>
      <div className={layoutClass}>
        <aside className="sidebar" id="sidebar" aria-label="Nawigacja główna">
          <div className="sidebar__brand">
            <img
              className="sidebar__logo"
              src="/static/img/logo.png"
              width={32}
              height={32}
              alt=""
              aria-hidden="true"
            />
            <span className="sidebar__brand-text">AgroHodowca</span>
          </div>
          <nav className="sidebar__nav">
            {SPA_NAV_SECTIONS.map((section) => (
              <div key={section.id} className="nav-section">
                <p className="nav-section__label">{section.label}</p>
                {section.items.map((item) =>
                  renderNavItem(
                    item,
                    location.pathname,
                    openGroups,
                    setOpenGroups,
                  ),
                )}
              </div>
            ))}
            {isPlatformAdmin ? (
              <div className="nav-section">
                <p className="nav-section__label">Admin</p>
                <NavLink
                  to="/admin"
                  data-testid="nav-admin"
                  className={({ isActive }) =>
                    isActive ? "nav-link nav-link--active" : "nav-link"
                  }
                >
                  <span className="nav-link__label">Panel admina</span>
                </NavLink>
              </div>
            ) : null}
          </nav>
          <div className="sidebar__footer">
            {SPA_NAV_FOOTER.map((item) =>
              item.to ? (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  data-testid={item.testId}
                  className={({ isActive }) =>
                    isActive ? "nav-link nav-link--active" : "nav-link"
                  }
                >
                  <span className="nav-link__label">{item.label}</span>
                </NavLink>
              ) : null,
            )}
            {!online ? (
              <p className="sidebar__offline" data-testid="sidebar-offline">
                Tryb offline
              </p>
            ) : null}
          </div>
        </aside>

        <div className="layout__main" ref={mainRef}>
          <header className="topbar">
            <button
              className="icon-btn"
              type="button"
              aria-label="Przełącz panel"
              aria-controls="sidebar"
              aria-expanded={mobileOpen}
              onClick={toggleSidebar}
            >
              <span className="icon-btn__bars" aria-hidden="true" />
            </button>
            <h1 className="topbar__title">{pageTitle(location.pathname)}</h1>
            <div className="topbar__spacer" />
            <div className="topbar__actions" role="group" aria-label="Akcje aplikacji">
              <SampleDataButton disabled={sampleBusy} onClick={() => void openSampleData()} />
              <ConnectionStatusButton online={online} onClick={() => setConnectionOpen(true)} />
              <a
                className="topbar__action"
                href="/app/logout"
                data-testid="logout-btn"
                title="Wyloguj"
                aria-label="Wyloguj"
                onClick={(event) => {
                  event.preventDefault();
                  void logout();
                }}
              >
                <svg
                  className="topbar__action-icon"
                  viewBox="0 0 24 24"
                  width="20"
                  height="20"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.75"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                  <polyline points="16 17 21 12 16 7" />
                  <line x1="21" y1="12" x2="9" y2="12" />
                </svg>
              </a>
            </div>
          </header>
          {entitlements?.messaging.is_beta ? (
            <BetaBanner messaging={entitlements.messaging} />
          ) : null}
          {block ? (
            <p className="banner banner--error" role="status" data-testid="pwa-update-block">
              {BLOCK_TEXT[block]}
            </p>
          ) : null}
          {appContentStatus?.active && appContentStatus.can_restore ? (
            <p className="banner banner--warn" role="status" data-testid="layout-app-content-restore">
              Dane aplikacji zostały wyczyszczone — możesz je przywrócić w{" "}
              <NavLink to="/settings">ustawieniach</NavLink> do{" "}
              {appContentStatus.purge_at
                ? new Date(appContentStatus.purge_at).toLocaleString("pl-PL")
                : "—"}
              .
            </p>
          ) : null}
          <div className="app__body">
            <Outlet key={contentKey} />
          </div>
        </div>
        <button
          type="button"
          className="backdrop"
          aria-label="Zamknij menu"
          tabIndex={mobileOpen ? 0 : -1}
          onClick={() => setMobileOpen(false)}
        />
        <SampleDataModal
          open={sampleOpen}
          initial={sampleInitial}
          busy={sampleBusy}
          setBusy={setSampleBusy}
          onDataChanged={() => setContentKey((key) => key + 1)}
          onClose={() => {
            setSampleOpen(false);
            setSampleInitial(null);
          }}
        />
        <ConnectionStatusModal
          online={online}
          open={connectionOpen}
          onClose={() => setConnectionOpen(false)}
        />
        <BackNavigationGuard />
        <OnlineToast show={showToast} />
      </div>
    </ModalHistoryProvider>
    </TagDisplayProvider>
  );
}
